/**
 * icons.tsx — VERBATIM port from ~/FrontendFinal/Design/src/components/icons.tsx.
 *
 * The three icons in the design (magnifier, column-sort mark, row schedule glyph),
 * extracted from the source SVGs — path/rect geometry copied verbatim, origin
 * normalised to a 0-based viewBox. Colours are `currentColor` so the caller
 * decides. See the Design repo's header for provenance. Imports nothing — no repoint.
 */

interface IconProps {
  className?: string;
  /** Rendered size in px. Defaults match the design's drawn size. */
  size?: number;
}

/** Magnifier — the search glyph. */
export function SearchIcon({ className = '', size = 11 }: IconProps) {
  return (
    <svg
      viewBox="0 0 11.025 11.025"
      width={size}
      height={size}
      className={className}
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M11.0251 10.4497L7.9523 7.377C8.6907 6.4906 9.0589 5.3536 8.9803 4.2026C8.9018 3.0516 8.3825 1.9752 7.5305 1.1974C6.6785 0.4195 5.5594 0 4.4061 0.0263C3.2527 0.0525 2.1539 0.5223 1.3381 1.3381C0.5223 2.1539 0.0525 3.2527 0.0263 4.4061C0 5.5594 0.4195 6.6785 1.1974 7.5305C1.9752 8.3825 3.0516 8.9018 4.2026 8.9803C5.3536 9.0589 6.4906 8.6907 7.377 7.9523L10.4497 11.0251L11.0251 10.4497ZM0.8532 4.5151C0.8532 3.7909 1.068 3.0829 1.4704 2.4807C1.8728 1.8785 2.4447 1.4091 3.1138 1.132C3.7829 0.8548 4.5192 0.7823 5.2295 0.9236C5.9398 1.0649 6.5923 1.4137 7.1044 1.9258C7.6166 2.4379 7.9653 3.0904 8.1066 3.8007C8.2479 4.5111 8.1754 5.2473 7.8982 5.9165C7.6211 6.5856 7.1517 7.1575 6.5495 7.5598C5.9473 7.9622 5.2394 8.177 4.5151 8.177C3.5443 8.1759 2.6135 7.7898 1.927 7.1033C1.2405 6.4168 0.8543 5.486 0.8532 4.5151Z" />
    </svg>
  );
}

/**
 * Notification bell — the Menu Bar's account-cluster icon (Figma node 7:315, the
 * icon-only 32×32 Button beside the search bar). Path lifted VERBATIM from that
 * button's exported SVG ("Vector", drawn in #161616); the rounded-rect background
 * from the export is dropped because the design-system Button supplies the 32×32
 * outlined square around it. The viewBox is kept at the source's full 0 0 32 32 so
 * the glyph sits at exactly the frame's origin/scale inside the button; `currentColor`
 * lets it inherit the button's icon colour (the icons.tsx convention — a literal
 * #161616 fill would also fail audit-tokens.mjs). `size` defaults to 20, the glyph's
 * drawn extent within the 32-box, so it reads centred in the sm icon button.
 */
export function BellIcon({ className = '', size = 20 }: IconProps) {
  return (
    <svg
      viewBox="0 0 32 32"
      width={size}
      height={size}
      className={className}
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M20.8873 17.5695L19.8462 16.4493V14.9655C19.8449 13.9401 19.4903 12.9516 18.8509 12.1912C18.2116 11.4308 17.3328 10.9524 16.3846 10.8486V10H15.6154V10.8486C14.6672 10.9524 13.7884 11.4308 13.1491 12.1912C12.5097 12.9516 12.1551 13.9401 12.1538 14.9655V16.4493L11.1127 17.5695C11.0405 17.6471 11 17.7523 11 17.8621V19.1035C11 19.2132 11.0405 19.3185 11.1127 19.3961C11.1848 19.4737 11.2826 19.5173 11.3846 19.5173H14.0769V19.8387C14.0686 20.3636 14.2406 20.8734 14.56 21.2704C14.8793 21.6673 15.3236 21.9235 15.8077 21.9899C16.0751 22.0184 16.345 21.9864 16.6002 21.896C16.8554 21.8056 17.0902 21.6587 17.2894 21.4647C17.4886 21.2708 17.648 21.0342 17.7571 20.77C17.8662 20.5059 17.9228 20.2201 17.9231 19.931V19.5173H20.6154C20.7174 19.5173 20.8152 19.4737 20.8873 19.3961C20.9595 19.3185 21 19.2132 21 19.1035V17.8621C21 17.7523 20.9595 17.6471 20.8873 17.5695ZM17.1538 19.931C17.1538 20.2603 17.0323 20.576 16.8159 20.8088C16.5995 21.0416 16.306 21.1724 16 21.1724C15.694 21.1724 15.4005 21.0416 15.1841 20.8088C14.9677 20.576 14.8462 20.2603 14.8462 19.931V19.5173H17.1538V19.931ZM20.2308 18.6897H11.7692V18.0334L12.8104 16.9132C12.8825 16.8357 12.9231 16.7304 12.9231 16.6207V14.9655C12.9231 14.0876 13.2473 13.2456 13.8243 12.6248C14.4013 12.0039 15.1839 11.6552 16 11.6552C16.8161 11.6552 17.5987 12.0039 18.1757 12.6248C18.7527 13.2456 19.0769 14.0876 19.0769 14.9655V16.6207C19.0769 16.7304 19.1175 16.8357 19.1896 16.9132L20.2308 18.0334V18.6897Z" />
    </svg>
  );
}

/** Sort glyph — the table header's column-sort mark. */
export function SortIcon({ className = '', size = 12 }: IconProps) {
  return (
    <svg
      viewBox="94 49 12 8"
      width={size}
      height={(size / 12) * 8}
      className={className}
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="103" y="49" width="3" height="3" />
      <rect x="99.5" y="50" width="1" height="6" />
      <rect x="100" y="50" width="3" height="1" />
      <rect x="94" y="54" width="3" height="3" />
      <rect x="97" y="55" width="3" height="1" />
    </svg>
  );
}

/** Schedule glyph — an alarm clock, drawn beside every table row's name. */
export function ScheduleIcon({ className = '', size = 12 }: IconProps) {
  return (
    <svg
      viewBox="94 132 12 12"
      width={size}
      height={size}
      className={className}
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M100.001 144C98.9128 144 97.8493 143.678 96.9447 143.073C96.04 142.469 95.335 141.61 94.9186 140.606C94.5023 139.601 94.3933 138.495 94.6056 137.429C94.8179 136.362 95.3418 135.382 96.1111 134.613C96.8804 133.844 97.8606 133.32 98.9276 133.108C99.9947 132.896 101.101 133.005 102.106 133.421C103.111 133.837 103.97 134.542 104.575 135.446C105.179 136.351 105.502 137.414 105.502 138.501C105.502 139.96 104.922 141.358 103.891 142.389C102.859 143.421 101.46 144 100.001 144ZM100.001 134.002C99.1106 134.002 98.2405 134.266 97.5003 134.761C96.7602 135.255 96.1833 135.958 95.8427 136.78C95.502 137.602 95.4129 138.506 95.5865 139.379C95.7602 140.252 96.1889 141.053 96.8183 141.683C97.4477 142.312 98.2497 142.74 99.1228 142.914C99.9958 143.087 100.901 142.998 101.723 142.658C102.546 142.317 103.248 141.741 103.743 141.001C104.238 140.261 104.502 139.391 104.502 138.501C104.502 137.308 104.027 136.164 103.183 135.32C102.339 134.476 101.194 134.002 100.001 134.002Z" />
      <path d="M101.296 140.501L99.5007 138.706V135.502H100.501V138.291L102.001 139.796L101.296 140.501Z" />
      <path d="M95.7909 132.004L94 133.798L94.708 134.505L96.4989 132.711L95.7909 132.004Z" />
      <path d="M104.209 132L103.501 132.706L105.292 134.5L106 133.794L104.209 132Z" />
    </svg>
  );
}
